import React, { useEffect, useMemo, useState } from 'react';
//lazy
const SideMenu = React.lazy(() => import('../HomePatient2/SideMenu'));
const ConfirmSwitchAccountPopup = React.lazy(
  () => import('../HomePatient2/SwitchProfile/ConfirmationPopup'),
);
const PatientStack = React.lazy(() => import('./Stacks/PatientStack'));
const StaffStack = React.lazy(() => import('./Stacks/StaffStack'));
// non-lazy
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { GetStaff, IsPhysician, IsManager, ActivePatient } from '../../careand-redux/selectors';
import { store, useAppSelector } from '../../careand-redux/store';
import { useAppDispatch } from '../../careand-redux/useAppDispatch';
import { navigationActions } from '../../careand-redux/slices/ui/navigation';
import ErrorBoundary from '../../components/Utils/ErrorBoundary';
import PatientError from '../../components/Utils/PatientError';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import { WithScrollRestoration } from '../../services/ScrollRestoration';
import useAnalytics from '../../hooks/useAnalytics';
import { LeftEdgeSwipeContainer } from './leftEdgeSwipeHandler';
import { Toaster as StaffToaster } from 'react-hot-toast';
import { default as PatientToaster } from '../../components/Toasts/Toaster';
import { ForceUpdateView } from '../HomePatient2/ForceUpdate';
import UnreadCountListener from './UnreadCountListener';
import { UserRoleEnum } from '../../generated/api';
import UnauthenticatedStack from './Stacks/UnauthenticatedStack';
import config from '../../config';
import { Device } from '../../services/Native/Device';
import NewVersionBanner from '../../components/Utils/NewVersionBanner';

const GenericApp = () => {
  const isLoggedIn = useAppSelector((state) => state.authentication.loggedIn);
  const twoFactorRequired = useAppSelector((state) => state.authentication.twoFactorRequired);
  const isPhysician = useAppSelector(IsPhysician);
  const isManager = useAppSelector(IsManager);
  const mainUserId = useAppSelector((state) => state.authentication.user?.id);
  const analyticsPayload = useAnalytics();
  const role = useAppSelector((state) => state.authentication.role);
  const isNativeMobile = Device.isMobile();
  const oldVersion = useAppSelector((state) => state.ui.oldVersion);

  usePathnameListener();

  const appClassName = useMemo(() => {
    const appName = role === 'patient' || role === undefined ? 'app-patient-2' : 'app-staff';
    const env = `${config.ENVIRONMENT}-app`;
    const nativeMobile = isNativeMobile ? 'native-mobile' : undefined;
    const loggedIn = isLoggedIn ? 'logged-in' : undefined;
    const twofa = twoFactorRequired ? ' hide-tab-bar' : undefined;
    const physician = isPhysician ? 'physician-app-staff' : undefined;
    const manager = isManager ? 'app-tenant' : undefined;

    return _([appName, env, nativeMobile, loggedIn, twofa, physician, manager]).compact().join(' ');
  }, [role, isNativeMobile, isLoggedIn]);

  return (
    <AnalyticsContext.Provider value={analyticsPayload}>
      <ErrorBoundary content={<PatientError />}>
        <WithScrollRestoration>
          <LeftEdgeSwipeContainer>
            {(leftEdgeSwipeHandler) => (
              <div
                className={appClassName}
                ref={leftEdgeSwipeHandler.leftEdgeSwipe.swipeableElement}
              >
                {!oldVersion && <NewVersionBanner />}
                <ForceUpdateView />
                {!role || role === 'patient' ? <PatientToaster /> : null}
                {role === 'patient' && (
                  <>
                    <UnreadCountListener userId={mainUserId} role={role} />
                    <SideMenu menuOpenSwipeHandler={leftEdgeSwipeHandler.menuOpenSwipeHandler} />
                    <ConfirmSwitchAccountPopup />
                  </>
                )}
                <MasterStack role={role} />
              </div>
            )}
          </LeftEdgeSwipeContainer>
        </WithScrollRestoration>
      </ErrorBoundary>
    </AnalyticsContext.Provider>
  );
};

function MasterStack({ role }: { role: UserRoleEnum | undefined }) {
  switch (role) {
    case undefined:
      return <UnauthenticatedStack />;
    case 'patient':
      return <PatientStack />;
    case 'physician':
    case 'admin':
    case 'phlebotomist':
    case 'manager':
      return <StaffStack />;
    case 'kiosk':
      return null;
  }
}

function usePathnameListener() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  useEffect(() => {
    history.listen((location) => {
      const url = location.pathname + location.search;
      dispatch(navigationActions.pathnameChanged(url));
    });
  }, []);
}

export default GenericApp;
